import i18n from '@/utils/i18n';
import { Icons } from '@usepepper/sprout-kit-ui';
import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { Pressable } from 'react-native';
import { ReactNode, useMemo } from 'react';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useSidebarToggleHandler } from '@experiences/templates/use-sidebar-toggle-handler';
import { useTheme } from '@/contexts/theme-context';

const ICON_SIZE = 20;

/** Sidebar toggle for `PageLayout.leadingActions`, shown whenever the shell owns a drawer or a collapsed sidebar. */
export const useSidebarToggleLeadingAction = (): ReactNode => {
  const theme = useTheme();
  const { layoutSize } = useLayoutContext();
  const isUsingDrawerLayout =
    layoutSize === LayoutSize.SMALL || layoutSize === LayoutSize.SMALL_TABLET;
  const isNativeBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;
  const isSidebarCollapsed = useExperienceUIStore(
    (state) => state.isSidebarCollapsed,
  );
  const handleToggle = useSidebarToggleHandler();

  const showToggleButton =
    !isNativeBottomNavLayout && (isUsingDrawerLayout || isSidebarCollapsed);

  return useMemo(() => {
    if (!showToggleButton) {
      return undefined;
    }
    const Icon = isUsingDrawerLayout ? Icons.Menu : Icons.PanelLeft;
    return (
      <Pressable
        accessibilityLabel={i18n.t('sales_portal.show_navigation')}
        accessibilityRole="button"
        onPress={handleToggle}
      >
        <Icon color={theme.sk.color.icon.secondary} size={ICON_SIZE} />
      </Pressable>
    );
  }, [handleToggle, isUsingDrawerLayout, showToggleButton, theme]);
};
